/**
 * iProov pre-flight support check — run before minting a session, so an unsupported
 * browser costs no token. Prompt-free: check() runs only device tests, never the
 * permission tests that checkWithPermission() would prompt for.
 *
 * Read out of the shipped iProovSupport.js, because two behaviours are easy to get wrong:
 *   1. `supported` gates on deviceTests ONLY (wasm/userMedia/videoInput/browser/
 *      localStorage). fullscreen and gyro run but do not gate it, so a gyro-less Android
 *      device reports supported here and still fails later at <iproov-me>.
 *   2. On iOS gyro is a permission test, so check() never runs it — no motion signal at all.
 *
 * `userMedia` is the valuable one: in a webview that cannot capture, getUserMedia is
 * ABSENT rather than denied. Deliberately rejects no browser on its own — iProov's own
 * gate decides, and VEX-7512 is an open bug from hand-rolled UA gating.
 *
 * `webgl` and `motionPermission` are ours, both telemetry-only — see their docstrings.
 */

import { logger } from "./logger";
import { classifyBrowser, type BrowserFamily } from "./browserFamily";

/** `unavailable` means the Permissions API couldn't answer, never that motion is blocked. */
export type MotionPermission = PermissionState | "unavailable";

/** The device tests `check()` runs, plus our `webgl`. For tagging; not exhaustive. */
export type IProovSupportTestName =
  | "wasm"
  | "userMedia"
  | "videoInput"
  | "browser"
  | "localStorage"
  | "fullscreen"
  | "gyro"
  | "frontCamera"
  | "webgl";

/** Raw shape returned by iProovSupport.check(). */
interface RawSupportResult {
  supported?: boolean;
  granted?: boolean;
  is_native_bridge?: boolean;
  flags?: { in_iframe?: boolean };
  /** check() spreads `this.tests` in here; the returned payload is the documented contract. */
  tests?: Partial<Record<IProovSupportTestName, boolean | null>>;
}

/** The checker's ua-parser-js result, exposed on the instance rather than check()'s return. */
interface RawDetector {
  browser?: { name?: string; version?: string; type?: string };
  device?: { type?: string; model?: string; vendor?: string };
  os?: { name?: string; version?: string };
}

interface RawSupportChecker {
  /** `{ emit: false }`: check() otherwise fires a misleading `denied` — see `granted` below. */
  check: (options?: { emit?: boolean }) => Promise<RawSupportResult>;
  tests?: Partial<Record<IProovSupportTestName, boolean | null>>;
  /** Public accessor; preferred over reading the `detector` field directly. */
  getDetector?: () => RawDetector | undefined;
  detector?: RawDetector;
}

export interface IProovSupportCheck {
  /** False means we failed open, so `supported` is a default rather than a measurement. */
  ran: boolean;
  /**
   * iProov's deviceTests AND our `webgl` check — fullscreen and gyro run but do not count,
   * per their gate. Still not equivalent to "the component will work"; read `failedTests`
   * too.
   */
  supported: boolean;
  /**
   * Always false after check() — permission tests never ran, so `.every()` is false. Means
   * "not established", NOT "denied". Only meaningful after checkWithPermission().
   */
  granted: boolean;
  isNativeBridge: boolean;
  /**
   * Per-test matrix, for telemetry. gyro is populated on Android only; on iOS it and
   * frontCamera are permission tests, so they stay null.
   */
  tests: Partial<Record<IProovSupportTestName, boolean | null>>;
  /** Tests that returned false, in the checker's own order. Empty when supported. */
  failedTests: IProovSupportTestName[];
  /** iProov's own frame check; cross-checks getFrameDepth(), which can return undefined. */
  inIframe?: boolean;
  /** Per the bundled ua-parser-js; "inapp" covers GSA / Instagram / Facebook / TikTok. */
  browserType?: string;
  /** "mobile" | "tablet" | "desktop", after iProov's desktop-mode correction. */
  deviceType?: string;
  /**
   * VEX-7682. `failedTests` says WHAT broke, this says WHICH remedy applies — userMedia
   * failing in Instagram means "Open in Safari"; the same failure on desktop does not.
   * Finer-grained than iProov's `browserType`, which only collapses to "inapp".
   */
  browserFamily: BrowserFamily;
  /** See BrowserClassification.reloadCanRePrompt — the VEX-7682 item 2 discriminator. */
  reloadCanRePrompt: boolean;
  /** Report-only. Never gates: a `denied` here still lets iProov prompt for itself. */
  motionPermission: MotionPermission;
  /** Populated only when `ran` is false: why we failed open. */
  failOpenReason?: "load_failed" | "timeout" | "check_threw";
}

/** Bounds the dynamic import on a slow connection, not the tests — those are fast. */
const CHECK_TIMEOUT_MS = 2000;

let iProovSupportClass: unknown = null;
let importPromise: Promise<unknown> | null = null;

/**
 * Dynamic on iProov's advice, so the full component isn't downloaded before support is
 * established. This is iProovSupport.js, a much smaller entrypoint than <iproov-me>.
 */
export async function loadIProovSupport(): Promise<unknown> {
  if (iProovSupportClass) return iProovSupportClass;
  if (typeof window === "undefined") return null;

  if (!importPromise) {
    importPromise = import("@iproov/web-sdk/iProovSupport.js")
      .then((module) => {
        iProovSupportClass = (module as { iProovSupport?: unknown }).iProovSupport ?? null;
        return iProovSupportClass;
      })
      .catch((err) => {
        // Reset so one CDN blip doesn't latch the gate off for the whole session.
        importPromise = null;
        throw err;
      });
  }

  return importPromise;
}

/** Prompt-free motion-sensor verdict, unlike DeviceMotionEvent.requestPermission (VEX-7881).
 *  `denied` is Brave's default-off Site Settings, which iProov reports as a capability failure. */
export async function getMotionPermission(): Promise<MotionPermission> {
  try {
    const permissions = navigator?.permissions;
    if (!permissions?.query) return "unavailable";

    // Not in TS's PermissionName union; Chromium has shipped it since the Generic Sensor API.
    const status = await permissions.query({ name: "accelerometer" as PermissionName });
    return status.state;
  } catch {
    return "unavailable";
  }
}

/** Telemetry only, never gates `supported` — 43% of flagged attempts passed (VEX-7881).
 *  Releases the context at once: browsers cap live contexts per page and evict the oldest. */
export function hasWebGL(): boolean {
  try {
    if (typeof document === "undefined") return true;

    const canvas = document.createElement("canvas");
    // A canvas is bound to the first context type requested, so webgl2 must come first.
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    if (!gl) return false;

    (gl as WebGLRenderingContext)
      .getExtension("WEBGL_lose_context")
      ?.loseContext();

    return true;
  } catch {
    // Fails open, like every other diagnostic here.
    return true;
  }
}

/** Resolved separately, so the probe can run concurrently with iProov's own check. */
type CheckWithoutMotion = Omit<IProovSupportCheck, "motionPermission">;

const FAIL_OPEN = (reason: IProovSupportCheck["failOpenReason"]): CheckWithoutMotion => {
  // webgl is ours, so it survives their bundle failing to load — but `ran: false` keeps
  // `supported` a default, so nothing recorded here can block.
  const browser = classifyBrowser();
  const webgl = hasWebGL();
  return {
    ran: false,
    supported: true,
    granted: false,
    isNativeBridge: false,
    tests: { webgl },
    failedTests: webgl ? [] : ["webgl"],
    browserFamily: browser.family,
    reloadCanRePrompt: browser.reloadCanRePrompt,
    failOpenReason: reason,
  };
};

/**
 * Fails open — every failure mode of the check itself resolves to `{ ran: false,
 * supported: true }`, so a diagnostic can never be why a verification fails. Do not switch
 * to checkWithPermission() without a user gesture and a priming screen; it prompts.
 */
export async function checkIProovSupport(
  timeoutMs: number = CHECK_TIMEOUT_MS
): Promise<IProovSupportCheck> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  // Started before the race, awaited after: it must not consume the 2s device-test budget.
  const motionPermission = getMotionPermission();

  try {
    const timeout = new Promise<CheckWithoutMotion>((resolve) => {
      timer = setTimeout(() => resolve(FAIL_OPEN("timeout")), timeoutMs);
    });

    const result = await Promise.race([runCheck(), timeout]);
    return { ...result, motionPermission: await motionPermission };
  } catch (err) {
    logger.warn("[iProovSupport] Pre-flight check failed, proceeding anyway:", err);
    return { ...FAIL_OPEN("check_threw"), motionPermission: await motionPermission };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

async function runCheck(): Promise<CheckWithoutMotion> {
  let SupportChecker: unknown;
  try {
    SupportChecker = await loadIProovSupport();
  } catch (err) {
    logger.warn("[iProovSupport] Failed to load support checker:", err);
    return FAIL_OPEN("load_failed");
  }

  if (typeof SupportChecker !== "function") {
    return FAIL_OPEN("load_failed");
  }

  // Pinned, not inherited, so an SDK default change can't move the support bar. Key is
  // camelCase — iProov's .d.ts says `assurance_type`, which would be silently ignored.
  const checker = new (SupportChecker as new (
    l: unknown,
    o?: { assuranceType?: string }
  ) => RawSupportChecker)(logger, { assuranceType: "genuine_presence" });

  const result = await checker.check({ emit: false });

  // Prefer the returned payload (documented contract); fall back to the instance field.
  // `webgl` is appended, not merged in first, so iProov's own reasons keep the
  // `failedTests[0]` primary slot that drives the PREFLIGHT_UNSUPPORTED reason and the
  // Sentry fingerprint — theirs are the more authoritative diagnosis when both fail.
  const webgl = hasWebGL();
  const tests = { ...(result?.tests ?? checker.tests ?? {}), webgl };
  const failedTests = (Object.keys(tests) as IProovSupportTestName[]).filter(
    (name) => tests[name] === false
  );

  const detector = checker.getDetector?.() ?? checker.detector ?? {};
  const browser = classifyBrowser();

  return {
    ran: true,
    // Theirs alone. `webgl` stays out: it passed 43% of the time it fired (VEX-7881).
    supported: result?.supported === true,
    granted: result?.granted === true,
    isNativeBridge: result?.is_native_bridge === true,
    tests,
    failedTests,
    browserFamily: browser.family,
    reloadCanRePrompt: browser.reloadCanRePrompt,
    inIframe: result?.flags?.in_iframe,
    // "standard" not undefined, so a missing tag is distinguishable from an ordinary browser.
    browserType: detector.browser?.type ?? "standard",
    deviceType: detector.device?.type ?? "desktop",
  };
}
